import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["namespace", "view", "outline", "children", "title", "id", "classes", "contentProps", "titleProps", "opened", "onClose", "onToggle", "icon", "confirmButton", "cancelButton", "onConfirm", "onCancel", "cancelProps", "confirmProps", "actions", "theme"];

var _iconsMap;

import React, { forwardRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { useMediaQuery } from "../hooks";
import { CheckCircleFilled, ChevronNormalDown, CloseSmallLinear, ErrorCircleFilled, InfoFiled, WarningFilled } from "../icons";
import { ButtonBase } from "../internal/ButtonBase";
import { TextBase } from "../internal/TextBase";
import { getMethodsForTestId } from "../utils/getMethodsForTestId";
import Button from '../Button';
import Title from '../Title';
import { views } from './constants';
import styles from './StatusMessage.css';
var cx = classNames.bind(styles);
var iconsMap = (_iconsMap = {}, _defineProperty(_iconsMap, views.default, InfoFiled), _defineProperty(_iconsMap, views.success, CheckCircleFilled), _defineProperty(_iconsMap, views.warning, WarningFilled), _defineProperty(_iconsMap, views.error, ErrorCircleFilled), _iconsMap);

var confirmViewMap = function confirmViewMap(theme) {
  var _ref;

  return _ref = {}, _defineProperty(_ref, views.default, theme || 'blue'), _defineProperty(_ref, views.success, theme || 'blue'), _defineProperty(_ref, views.warning, 'warning'), _defineProperty(_ref, views.error, 'destructive'), _ref;
};

export var StatusMessage = /*#__PURE__*/forwardRef(function StatusMessage(_ref2, ref) {
  var namespace = _ref2.namespace,
      _ref2$view = _ref2.view,
      view = _ref2$view === void 0 ? views.default : _ref2$view,
      _ref2$outline = _ref2.outline,
      outline = _ref2$outline === void 0 ? false : _ref2$outline,
      children = _ref2.children,
      title = _ref2.title,
      id = _ref2.id,
      _ref2$classes = _ref2.classes,
      classes = _ref2$classes === void 0 ? defaultPropObject : _ref2$classes,
      contentProps = _ref2.contentProps,
      titleProps = _ref2.titleProps,
      _ref2$opened = _ref2.opened,
      opened = _ref2$opened === void 0 ? false : _ref2$opened,
      onClose = _ref2.onClose,
      onToggle = _ref2.onToggle,
      icon = _ref2.icon,
      confirmButton = _ref2.confirmButton,
      cancelButton = _ref2.cancelButton,
      onConfirm = _ref2.onConfirm,
      onCancel = _ref2.onCancel,
      cancelProps = _ref2.cancelProps,
      confirmProps = _ref2.confirmProps,
      actions = _ref2.actions,
      theme = _ref2.theme,
      props = _objectWithoutProperties(_ref2, _excluded);

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'statusMessage'),
      getTestId = _getMethodsForTestId.getTestId,
      getNamespace = _getMethodsForTestId.getNamespace;

  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;
  var Icon = iconsMap[view];
  var isToggle = Boolean(onToggle);
  var isClose = Boolean(onClose);
  var isConfirm = Boolean(onConfirm);
  var isCancel = Boolean(onCancel);
  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    role: "alert",
    ref: ref,
    className: cx('root', "view_".concat(view), {
      outline: outline,
      has_close_or_toggle: isToggle || isClose
    }, classes.root)
  }, getTestId()), isToggle ? /*#__PURE__*/React.createElement(ButtonBase, {
    className: cx('toggle', {
      open: opened
    }, classes.toggle),
    onClick: onToggle,
    "aria-expanded": opened,
    "aria-controls": id,
    "aria-label": "toggler",
    namespace: getNamespace('toggle')
  }, /*#__PURE__*/React.createElement(ChevronNormalDown, {
    "aria-hidden": true
  })) : isClose && /*#__PURE__*/React.createElement(ButtonBase, {
    className: cx('close', classes.close),
    "aria-label": "close",
    onClick: onClose,
    namespace: getNamespace('close')
  }, /*#__PURE__*/React.createElement(CloseSmallLinear, {
    "aria-hidden": true
  })), /*#__PURE__*/React.createElement("div", {
    className: cx('icon', classes.icon)
  }, icon || /*#__PURE__*/React.createElement(Icon, {
    "aria-hidden": true,
    size: "md"
  })), title && /*#__PURE__*/React.createElement(Title, _extends({}, titleProps, {
    level: 5
  }), title), /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: cx('content'),
    in: isToggle ? opened : true,
    timeout: 300,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    id: isToggle ? id : undefined,
    className: cx('content', {
      has_title: title,
      has_children: children
    }, classes.content)
  }, /*#__PURE__*/React.createElement(TextBase, _extends({}, contentProps, {
    as: "div",
    size: "m",
    color: "N900"
  }), children), (isConfirm || isCancel || actions) && /*#__PURE__*/React.createElement("div", {
    className: cx('actions')
  }, isConfirm && /*#__PURE__*/React.createElement("div", {
    className: cx('button')
  }, /*#__PURE__*/React.createElement(Button, _extends({}, confirmProps, {
    theme: confirmViewMap(theme)[view],
    block: true,
    size: isMobile ? 'small' : 'medium',
    onClick: onConfirm,
    classes: {
      root: cx(classes.confirm, confirmProps === null || confirmProps === void 0 ? void 0 : confirmProps.classes)
    },
    namespace: getNamespace('confirm')
  }), confirmButton)), isCancel && /*#__PURE__*/React.createElement("div", {
    className: cx('button')
  }, /*#__PURE__*/React.createElement(Button, _extends({}, cancelProps, {
    block: true,
    theme: outline ? 'secondary' : 'white',
    size: isMobile ? 'small' : 'medium',
    onClick: onCancel,
    classes: {
      root: cx(classes.cancel, cancelProps === null || cancelProps === void 0 ? void 0 : cancelProps.classes)
    },
    namespace: getNamespace('cancel')
  }), cancelButton)), actions))));
});
export default StatusMessage;